"use client"

import {
  type BrandTheme,
  type MerchantBranding,
  useBrandTheme,
} from "@/app/context/brand-theme-context"
import {
  loadGoogleFont,
  preloadFont,
  validateBrandFont,
} from "@/app/utils/font-validation"
import { useEffect } from "react"

interface BrandThemeProviderProps {
  merchantBranding?: MerchantBranding
  pageTheme?: BrandTheme
  children: React.ReactNode
}

export function BrandThemeProvider({
  merchantBranding,
  pageTheme,
  children,
}: BrandThemeProviderProps) {
  useEffect(() => {
    // Determine which theme to use for local styling
    const theme =
      pageTheme ||
      (merchantBranding
        ? {
            primaryColor: merchantBranding.colors.primaryColor,
            secondaryColor: merchantBranding.colors.secondaryColor,
            backgroundColor: merchantBranding.colors.bgColor,
            textColor: merchantBranding.colors.textColor,
            fontFamily: merchantBranding.font,
          }
        : null)

    // Apply CSS custom properties to the document root
    const root = document.documentElement

    // Professional brand font handling with validation
    const fontValidation = validateBrandFont(theme?.fontFamily)

    // Always set the font stack (includes fallbacks)
    root.style.setProperty("--brand-font-family", fontValidation.fontStack)

    // Load Google Font if needed and valid
    if (fontValidation.isValid && fontValidation.needsLoading) {
      loadGoogleFont(fontValidation.sanitizedName)
        .then(() => {})
        .catch((error) => {
          console.warn(
            `Failed to load brand font: ${fontValidation.sanitizedName}`,
            error,
          )
          // Font stack already includes fallbacks, so UI won't break
        })
    }

    // Log validation result for debugging
    if (!fontValidation.isValid && theme?.fontFamily) {
      console.warn(
        `Brand font validation failed: ${fontValidation.reason}. Using fallback: ${fontValidation.fontStack}`,
      )
    }

    // Only apply brand theme colors if we have valid brand colors
    if (!theme || !theme.backgroundColor || !theme.textColor) {
      // No valid brand colors - ensure we're using default app colors
      root.removeAttribute("data-brand-theme")
      root.style.removeProperty("--brand-primary")
      root.style.removeProperty("--brand-secondary")
      root.style.removeProperty("--brand-bg")
      root.style.removeProperty("--brand-text")

      // Reset body color styles to use app defaults (but keep font)
      document.body.style.backgroundColor = ""
      document.body.style.color = ""
      return
    }

    // Only set color properties if the brand colors are defined
    if (theme.primaryColor) {
      root.style.setProperty("--brand-primary", theme.primaryColor)
    }
    if (theme.secondaryColor) {
      root.style.setProperty("--brand-secondary", theme.secondaryColor)
    }
    if (theme.backgroundColor) {
      root.style.setProperty("--brand-bg", theme.backgroundColor)
    }
    if (theme.textColor) {
      root.style.setProperty("--brand-text", theme.textColor)
    }

    // Only set the brand theme flag if we have essential colors
    if (theme.backgroundColor && theme.textColor) {
      root.setAttribute("data-brand-theme", "true")
    }

    // Apply background color to the body for full page theming (only if defined)
    if (theme.backgroundColor) {
      document.body.style.backgroundColor = theme.backgroundColor
    }
    if (theme.textColor) {
      document.body.style.color = theme.textColor
    }

    // Cleanup function to reset styles when component unmounts
    return () => {
      // Clear CSS custom properties
      root.style.removeProperty("--brand-primary")
      root.style.removeProperty("--brand-secondary")
      root.style.removeProperty("--brand-bg")
      root.style.removeProperty("--brand-text")
      root.style.removeProperty("--brand-font-family")
      root.removeAttribute("data-brand-theme")

      // Reset body styles to app defaults
      document.body.style.backgroundColor = ""
      document.body.style.color = ""
      document.body.style.fontFamily = ""
    }
  }, [merchantBranding, pageTheme])

  // Apply inline styles to the container for immediate effect (only if brand colors are defined)
  const theme =
    pageTheme ||
    (merchantBranding
      ? {
          primaryColor: merchantBranding.colors.primaryColor,
          secondaryColor: merchantBranding.colors.secondaryColor,
          backgroundColor: merchantBranding.colors.bgColor,
          textColor: merchantBranding.colors.textColor,
          fontFamily: merchantBranding.font,
        }
      : null)

  // Apply container styles - colors only if available
  const containerStyle: React.CSSProperties = {
    minHeight: "100vh",
    ...(theme?.backgroundColor &&
      theme?.textColor && {
        backgroundColor: theme.backgroundColor,
        color: theme.textColor,
      }),
    // Font is handled via CSS custom properties and globals.css
  }

  return (
    <div style={containerStyle} className="brand-theme-container">
      {children}
    </div>
  )
}

// Legacy hook for backward compatibility - now uses the global context
export function useBrandThemeValues() {
  return {
    background: "var(--brand-bg, #43004A)",
    text: "var(--brand-text, #FFFFFF)",
    font: 'var(--brand-font-family, "Inter")',
  }
}
